import type { ImportResult } from "../types";

/**
 * Split raw CSV into "valid" and "failed" CSV text by 1-based data-row number.
 * Both outputs keep the original header (and exact row bytes), so the valid text
 * can be committed and the failed text handed back to the user to fix and
 * re-upload. `isFailed(rowNumber)` decides which side each data row lands on.
 */
export function partitionCsvByRows(
  csvText: string,
  isFailed: (rowNumber: number) => boolean,
): { validCsv: string; failedCsv: string; validCount: number; failedCount: number } {
  const normalised = csvText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalised.split("\n");
  if (lines.length === 0 || lines[0].trim() === "") {
    return { validCsv: "", failedCsv: "", validCount: 0, failedCount: 0 };
  }

  const header = lines[0];
  const dataLines = lines.slice(1).filter((l) => l.trim() !== "");

  const valid: string[] = [];
  const failed: string[] = [];
  dataLines.forEach((line, i) => {
    (isFailed(i + 1) ? failed : valid).push(line);
  });

  return {
    validCsv: [header, ...valid].join("\n"),
    failedCsv: [header, ...failed].join("\n"),
    validCount: valid.length,
    failedCount: failed.length,
  };
}

/**
 * Combine per-chunk `ImportResult`s into a single committed result. Committed and
 * failed counts are summed; each chunk's error row numbers are shifted by that
 * chunk's `offset` back to original-file row numbers.
 */
export function aggregateChunkResults(
  parts: { result: ImportResult; offset: number }[],
): ImportResult {
  let committed = 0;
  let failed = 0;
  const errors = [];

  for (const { result, offset } of parts) {
    committed += result.committed;
    failed += result.failed;
    for (const e of result.errors) {
      errors.push({ ...e, row: e.row + offset });
    }
  }

  return {
    status: "committed",
    committed,
    failed,
    errors,
    unmapped: [],
    mappingsSaved: 0,
  };
}
